import { v4 as uuid } from 'uuid';
import { assign } from 'lodash';
import { ChangeDetectionStrategy, Component, type OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "primeng/dynamicdialog";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatDesigns from "@medsurf/flat-designs";
import * as FlatFacades from '@medsurf/flat-facades';
import { ZodSharedValidator } from '@medsurf/flat-validators';
import { ModalControlComponent } from "@medsurf/flat-components";

@Component({
    selector: 'medsurf-stain-detail',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './stain-detail.component.html',
    styleUrls: ['./stain-detail.component.scss'],
    standalone: false
})
export class StainDetailComponent implements OnInit {
  @ViewChild('nameInputText', {static: false}) nameInputText: FlatDesigns.TextInputFormControlDesign;
  
  isSubmitted = signal(false);
  #stain: FlatModels.StainEntityModels.Stain | null;
  stainForm = new FormGroup(
    {
      name: new FormControl(''),
      shortcut: new FormControl(''),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.StainEntityModels.StainSchema
      ),
      updateOn: 'change',
    }
  );
  readonly FlatModels = FlatModels;

  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
              public navigationControlFacade: FlatFacades.NavigationControlFacade,
              public constantAuthorFacade: FlatFacades.ConstantAuthorFacade,
              public dialogService: DialogService,
              public translate: TranslateService) {
    // Update User
    this.constantAuthorFacade.stain$.pipe(takeUntilDestroyed()).subscribe((stain) => {
      this.#stain = structuredClone(stain);
      if (this.#stain) {
        this.stainForm.reset(this.#stain);
      }
      this.isSubmitted.set(false);
    });
  }

  ngOnInit() {
    // Autofocus
    setTimeout(() => {
      this.nameInputText?.inputElement?.nativeElement?.focus();
    });
  }

  onSubmit() {
    if (this.isSubmitted() || this.stainForm.invalid) {
      return;
    }
    this.isSubmitted.set(true);

    if (this.#stain) {
      // Update Stain
      const stain = assign(this.#stain, this.stainForm.value);
      this.constantAuthorFacade.requestUpdateStain(stain).subscribe({
        error: (result) => {
          this.isSubmitted.set(false);
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.stainForm);
        },
        next: () => {
          this.isSubmitted.set(false);
          this.alertControlFacade.requestAddAlert({
            key: 'main',
            detail: 'notification.saved',
            severity: FlatModels.AlertControlModels.AlertType.enum.success,
            life: FlatModels.AlertControlModels.AlertTime.DEFAULT
          });
        }
      });
    } else {
      // Add Stain
      const stain = {
        id: uuid(),
        name: this.stainForm.value.name || "",
        shortcut: this.stainForm.value.shortcut || '',
      };
      this.constantAuthorFacade.requestAddStain(stain).subscribe({
        error: (result) => {
          this.isSubmitted.set(false);
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.stainForm);
        },
        next: () => {
          this.isSubmitted.set(false);
          this.navigationControlFacade.requestNavigate(['stain', stain.id]);
          this.alertControlFacade.requestAddAlert({
            key: 'main',
            detail: 'notification.saved',
            severity: FlatModels.AlertControlModels.AlertType.enum.success,
            life: FlatModels.AlertControlModels.AlertTime.DEFAULT
          });
        }
      });
    }
  }

  deleteStain() {
    if (!this.#stain) return;
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.stain_remove'),
      data: {
        label: 'global.stain_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(remove => {
      if (this.#stain && remove) {
        const stain = this.#stain;
        this.constantAuthorFacade.requestRemoveStain(stain).subscribe({
          error: (result) => {
            ZodSharedValidator.handleError(result, this.alertControlFacade, this.stainForm);
          },
          next: () => {
            this.navigationControlFacade.requestNavigate(['stains']);
            this.alertControlFacade.requestAddAlert({
              key: 'main',
              detail: 'notification.saved',
              severity: FlatModels.AlertControlModels.AlertType.enum.success,
              life: FlatModels.AlertControlModels.AlertTime.DEFAULT
            });
          }
        });
      }
    });
  }
}
